import { Injectable } from '@angular/core';
import { Response } from '@angular/http';
import { Router } from '@angular/router';
import { HttpClient, HttpResponse, HttpRequest, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { Observable, of, Subject } from 'rxjs';
import { AssetHierarchyItem } from '../module/assets/view/components/asset-hierarchical-view/asset-hierarchical-view.component';
import { ManageAssetData } from '../module/manage-assets/view/view.component';
import { LooseObject } from '../objects/loose-object';
//Get the base url from the environment file.
import { environment } from '../../environments/environment';
import { ApiService } from './api.service';
import { AssetActualFault } from '../module/assets/types';
import {
	arr_map,
	blank,
	data_get,
	fallback,
	filled,
	format_datetime,
	OptionalValue,
	to_local_datetime,
	transform,
	isId,
} from '../shared/utils/common';
import { get_severity_level_label } from '../module/assets/utils';

const kBaseUrl: string = environment.url + "/assets_job/";


@Injectable()
export class AssetService {
	private _variantInputChange = new Subject<string>();

	changedVariantInput$: Observable<string> = this._variantInputChange.asObservable();

	constructor(
		private http: HttpClient,
		private readonly _api: ApiService,
	) { }

	// Link multiple assets to a job using ids of assets
	linkAssets(arAssetIds, strJobId) {
		let body = new URLSearchParams();
		body.append('asset_ids', JSON.stringify(arAssetIds));
		body.append('job_id', strJobId);

		return this.http.post<Response>(kBaseUrl + "link_assets", body.toString());
	}

	// Link multiple assets to a job
	linkAllRelatedAssets(arFilter, strJobId) {
		let body = new URLSearchParams();
		body.append('filters', JSON.stringify(arFilter));
		body.append('job_id', strJobId);

		return this.http.post<Response>(kBaseUrl + "link_related_assets", body.toString(), { observe: 'response' });
	}

	// Get linked assets against the current module
	getAllLinkedAssets(strRecordId, strModule) {
		let body = new URLSearchParams();
		body.append('module', strModule);
		body.append('record_id', strRecordId);

		return this.http.post<Response>(kBaseUrl + "get_linked_assets", body.toString());
	}

	// Get list of asset jobs that have faults
	getAllFaultyAssets(arJobRecord) {
		let body = new URLSearchParams();
		body.append('job_record', arJobRecord);

		return this.http.post<Response>(kBaseUrl + "get_faulty_assets", body.toString());
	}

	// Get all asset jobs record with realationship with the given opportunity id
	// Re-create those asset jobs in the new job
	getOpportunityAssets(strNewJobId, strOldJobId, strOpportunityId) {
		let body = new URLSearchParams();
		body.append('new_job_id', strNewJobId);
		body.append('old_job_id', strOldJobId);
		body.append('opportunity_id', strOpportunityId);

		return this.http.post<Response>(kBaseUrl + "convert_to_quote", body.toString());
	}

	/**
	 * Gets the hierarchical view. The value returned is a flat one-dimensional array,
	 * the nesting being determined by the 'level' field on each item.
	 *
	 * @param {string} strAssetId
	 *
	 * @returns {Observable<AssetHierarchyItem[]>}
	 */
	getHierarchicalView(strAssetId: string): Observable<{ data: AssetHierarchyItem[] }> {
		return this.http.post<{ data: AssetHierarchyItem[] }>(
			`${environment.url}/assets/hierarchical_view`,
			new URLSearchParams({
				asset_id: strAssetId,
			})
		);
	}

	exportManageAssetsCsv(strAssetTypeId: string, strSiteId: string, strCustomerId: string|null, strJobId: string|null, data: LooseObject[]): Observable<HttpResponse<Blob>> {
    return this.http.post<Blob>(
			`${environment.url}/assets/export_manage_assets`,
      new URLSearchParams({
        data: JSON.stringify(data),
				...(isId(strCustomerId) && { customer_id: strCustomerId }),
				...(isId(strSiteId) && { site_id: strSiteId }),
				...(isId(strAssetTypeId) && { asset_type_id: strAssetTypeId }),
				...(isId(strJobId) && { job_id: strJobId }),
      }),
      { headers: new HttpHeaders({ Accept: 'text/csv' }), responseType: 'blob' as 'json', observe: 'response' }
    )
	}

	assetsForJob(strAssetTypeId: string, strJobId: string, strSiteId: string|null, strCustomerId: string|null) {
		return this._api.callPrivate$({
			path: '/assets/assets_for_job',
			action: 'post',
			body: {
				asset_type_id: strAssetTypeId,
				job_id: strJobId,
				...(isId(strSiteId) && { site_id: strSiteId }),
				...(isId(strCustomerId) && { customer_id: strCustomerId }),
			},
			onSuccessResponse: (response) => {
				return fallback(
					response.get('data'),
					{
						fallback: () => []
					}
				);
			}
		});
	}

	assetCounts(strJobId: string|null, strSiteId: string|null, strCustomerId: string|null, strAssetTypeId: string|null) {
		return this._api.callPrivate$({
			path: '/assets/asset_counts',
			action: 'post',
			body: {
				...(isId(strAssetTypeId) && { asset_type_id: strAssetTypeId }),
				...(isId(strJobId) && { job_id: strJobId }),
				...(isId(strCustomerId) && { customer_id: strCustomerId }),
				...(isId(strSiteId) && { site_id: strSiteId }),
			},
			onSuccessResponse: (response) => {
				return fallback(
					response.get('data'),
					{
						fallback: () => []
					}
				);
			}
		});
	}

	updateViaManageAssets(arAssets: ManageAssetData[], arAssetIdsToDelete: string[], strJobId: string|null): Observable<string[]> {
		return this._api.callPrivate$({
			path: '/assets/manage_assets_update',
			action: 'post',
			body: {
				...(isId(strJobId) && { job_id: strJobId }),
				assets: JSON.stringify(arAssets),
				asset_ids_to_delete: JSON.stringify(arAssetIdsToDelete),
			},
			onSuccessResponse: (response) => {
				return fallback(
					response.get('data'),
					{
						fallback: () => []
					}
				);
			}
		});
	}

	getFaults(args: {
		id?: OptionalValue<string>;
		sort_by_criticality?: boolean;
		filter?: string;
	}): Observable<AssetActualFault[]> {
		args = Object.assign({
			sort_by_criticality: false,
		}, args);

		if (blank(args.id)) {
			return of([]);
		}

		return this._api.callPrivate$({
			path: '/assets/faults',
			action: 'post',
			body: {
				'asset_id': args.id,
				'sort_by_criticality': fallback(args.sort_by_criticality, {
					fallback: () => false,
				}),
				... (filled(args.filter) && {
					'filter': args.filter,
				}),
			},
			onSuccessResponse: (response) => arr_map(response.get('items'), {
				transformer: (fault) => ({
					...fault,
					severity: fallback(data_get(fault, 'severity'), {
						fallback: () => get_severity_level_label(data_get(fault, 'severity_level')),
					}),
					date_reported: transform(data_get(fault, 'date_reported'), {
						transformer: (value) => format_datetime(to_local_datetime(value), {
							format: 'YYYY-MM-DD HH:mm:ss',
						}),
					}),
					date_resolved: transform(data_get(fault, 'date_resolved'), {
						transformer: (value) => format_datetime(to_local_datetime(value), {
							format: 'YYYY-MM-DD HH:mm:ss',
						}),
					}),
					date_acknowledged: transform(data_get(fault, 'date_acknowledged'), {
						transformer: (value) => format_datetime(to_local_datetime(value), {
							format: 'YYYY-MM-DD HH:mm:ss',
						}),
					}),

				}),
			}),
		});
	}

	variantInputChanged(strValue: string): void {
		this._variantInputChange.next(strValue);
	}
}

export type MatchingAssetData = {
  id: string;
  serial_number: string;
  site_id: string;
  customer_id: string;
}